import { Alert, Button } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { customersApi, type IbanqSubAccountView } from '../../api/customers.api.js';
import { APP_LOCALE } from '../../utils/locale.js';

/**
 * Where the customer's IBANQ sub-account stands (DEV-149 phase 2).
 *
 * An account row says "Awaiting IBAN" and nothing more. This says whether
 * IBANQ is still reviewing, has stalled, or refused — with the reason — and
 * lets an account opener ask IBANQ now. Nothing is shown for a customer with
 * no IBANQ request, or once the sub-account is active (the rows say it then).
 */
export function IbanqSubAccountStatus({ customerId, canRefresh }: { customerId: string; canRefresh: boolean }): JSX.Element | null {
  const queryClient = useQueryClient();
  const key = ['customer', customerId, 'ibanq-sub-account'];
  const query = useQuery({ queryKey: key, queryFn: () => customersApi.ibanqSubAccount(customerId), staleTime: 30_000 });
  const refresh = useMutation({
    mutationFn: () => customersApi.refreshIbanqSubAccount(customerId),
    onSuccess: async (view) => {
      queryClient.setQueryData(key, view);
      await queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
    },
  });
  const view: IbanqSubAccountView | null | undefined = query.data;
  // Anything that is not a request (none made, or an unexpected body) shows nothing.
  if (view === null || view === undefined || typeof view.status !== 'string' || view.state === 'ACTIVE') return null;

  const when = (iso: string | null): string => (iso === null ? 'not yet' : new Date(iso).toLocaleString(APP_LOCALE));
  const action = canRefresh ? (
    <Button color="inherit" size="small" disabled={refresh.isPending} onClick={() => refresh.mutate()}>
      {refresh.isPending ? 'Checking…' : 'Check now'}
    </Button>
  ) : undefined;

  if (view.state === 'REFUSED') {
    return (
      <Alert severity="error" action={action}>
        IBANQ {view.status === 'Rejected' ? 'rejected' : 'could not open'} this customer’s sub-account
        {view.rejectionReason ? `: ${view.rejectionReason}` : ''}{view.rejectionDetail ? ` (${view.rejectionDetail})` : ''}.
        Their IBANQ accounts cannot be paid into until it is resolved with IBANQ.
      </Alert>
    );
  }
  return (
    <Alert severity={view.state === 'STALLED' ? 'warning' : 'info'} action={action}>
      IBANQ sub-account {view.status === 'SENDING' ? 'request not yet answered' : view.status.toLowerCase()} since {when(view.requestedAt)}
      {view.state === 'STALLED' ? ' — longer than an hour, an alert was raised' : ''}. Last checked {when(view.lastCheckedAt)}
      {view.nextCheckAt !== null ? `, next check ${when(view.nextCheckAt)}` : ''}.
      {view.lastError ? ` Last problem: ${view.lastError}` : ''}
      {refresh.isError ? ' Checking failed; try again shortly.' : ''}
    </Alert>
  );
}
